<template>
    <div>
        <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-1 table-title">Current User Statistics - {{ companyCurrency }}</h4>
            <div class="d-flex ">
                <span class="text-danger mr-1 cursor-pointer" v-if="isFilterShown" @click="clearFilter"
                    >Clear Search</span
                >
                <font-awesome-icon
                    :icon="['far', 'filter']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="'Filter'"
                    @click="toggleFilter"
                />
                <font-awesome-icon
                    :icon="['far', 'compress-arrows-alt']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="`Filter out 0 values: ${isFilterZeroShown ? 'on' : 'off'}`"
                    @click="toggleFilterZero"
                />
                <font-awesome-icon
                    :icon="['far', 'cog']"
                    class="btn-icon mr-1"
                    v-b-tooltip.hover.top="'Table Settings'"
                    v-b-modal.cus
                />
                <font-awesome-icon
                    :icon="['far', 'file-export']"
                    class="btn-icon"
                    v-b-tooltip.hover.top="'Export CSV'"
                    @click="$bvModal.show('cus-download-modal')"
                />
                <b-modal
                    id="cus-download-modal"
                    title="Choose format type"
                    centered
                    @ok="exportFile(exportData, exportFormat, 'CurrentUserStatistics')"
                >
                    <v-select v-model="exportFormat" :clearable="false" label="format" :options="['csv', 'xls']" />
                </b-modal>
            </div>
        </div>
        <b-card no-body class="px-50 my-shadow">
            <CommonTable
                :isLoading="isLoading"
                :fields="fields"
                :items="dataAfterCollapsed"
                :max-display-rows="maxDisplayRowCount"
                :isFilterShown="isFilterShown"
                :isFilterZeroShown="isFilterZeroShown"
                @filterChange="filterChange"
                @filterClear="filterClear"
                :canFilterOutZeroValues="canFilterOutZeroValues"
                @filterOutZeroValues="handleFilterOutZeroValues"
                :field_sort="field_sort"
                :isDesc="isDesc"
                @onHeaderClick="(field) => sortBy(field)"
                ref="table"
            >
                <template #summary-row>
                    <b-tr class="text-right">
                        <b-td class="bg-black text-center">Total</b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('Group')" class="bg-black text-right"></b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('Current Credit')" class="bg-black">{{
                            formatMoney(dataTotal.CurrentCredit, 2)
                        }}</b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('Current Equity')" class="bg-black">{{
                            formatMoney(dataTotal.CurrentEquity, 2)
                        }}</b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('Withdrawable')" class="bg-black">{{
                            formatMoney(dataTotal.Withdrawable, 2)
                        }}</b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('P/L Today')"
                            class="bg-black"
                            :class="dataTotal.ActualPL >= 0 ? 'text-success' : 'text-danger'"
                            >{{ formatMoney(dataTotal.ActualPL, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('%Change Equity')"
                            class="bg-black"
                            :class="dataTotal['%ChangeEquity'] >= 0 ? ' text-success' : ' text-danger'"
                            >{{ formatMoney(dataTotal["%ChangeEquity"], 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('P/L Yesterday')"
                            class="bg-black"
                            :class="dataTotal.ActualPL >= 0 ? 'text-success' : 'text-danger'"
                            >{{ formatMoney(dataTotal.PLYesterday, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('P/L This Month')"
                            class="bg-black"
                            :class="dataTotal.PLThisMonth >= 0 ? 'text-success' : 'text-danger'"
                        >
                            {{ formatMoney(dataTotal.PLThisMonth, 2) }}
                        </b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('P/L Last Month')"
                            class="bg-black"
                            :class="dataTotal.PLLastMonth >= 0 ? ' text-success' : ' text-danger'"
                            >{{ formatMoney(dataTotal.PLLastMonth, 2) }}</b-td
                        >
                        <b-td
                            v-if="fields.map((i) => i.label).includes('P/L Overall')"
                            class="bg-black"
                            :class="dataTotal.PLOverall >= 0 ? 'text-success' : 'text-danger'"
                            >{{ formatMoney(dataTotal.PLOverall, 2) }}</b-td
                        >
                        <b-td v-if="fields.map((i) => i.label).includes('#Lots This Month')" class="bg-black">{{
                            formatMoney(dataTotal["#LotsThisMonth"], 2)
                        }}</b-td>
                        <b-td v-if="fields.map((i) => i.label).includes('%Profitable Closed Trade')" class="bg-black">{{
                            formatMoney(dataTotal["%ProfitableClosedTrade"], 2)
                        }}</b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('AVG Profit per Trade This Month')"
                            class="bg-black"
                            >{{ formatMoney(dataTotal["AVGProfitPerClosedTradeThisMonth"], 2) }}</b-td
                        >
                        <b-td v-if="fields.map((i) => i.label).includes('%Profitable Total Trade')" class="bg-black">{{
                            formatMoney(dataTotal["%ProfitableTotalTrade"], 2)
                        }}</b-td>
                        <b-td
                            v-if="fields.map((i) => i.label).includes('AVG Floating P/L per Open Trade')"
                            class="bg-black"
                            >{{ formatMoney(dataTotal.AVGFloatingPLPerOpenTrade, 2) }}</b-td
                        >
                    </b-tr>
                </template>

                <template #cell(name)="{ item }">
                    <div :style="{ 'padding-left': `${calcNamePadding(item.level)}px` }">
                        <dot v-if="isItemUncollapsible(item)" />
                        <span v-else class="cursor-pointer">
                            <font-awesome-icon
                                :icon="['fas', 'caret-down']"
                                size="lg"
                                class="mr-50"
                                @click="collapse(item)"
                                v-if="!collapsedSet.includes(item[whichpath])"
                            />
                            <font-awesome-icon
                                :icon="['fas', 'caret-right']"
                                size="lg"
                                class="mr-50"
                                @click="uncollapse(item)"
                                v-else
                            />
                        </span>
                        {{ item.name }}
                        <!-- <NameField :name="item.name" /> -->
                    </div>
                </template>
            </CommonTable>
            <div class="py-1 pr-1 d-flex justify-content-between card-footer-color">
                <FilterPills
                    :isLoading="isLoading"
                    v-model="activeLevels"
                    :levels="levels"
                    @toggleLevel="toggleLevel"
                />
                <div class="d-flex align-items-center">
                    <div class="mr-3 text-heading-color">
                        Accounts: {{ dataAfterCollapsed.filter((item) => isItemUncollapsible(item)).length }}
                    </div>
                    <RowCountPill v-model="maxDisplayRowCount" />
                </div>
            </div>
        </b-card>
        <TableSettingsModal
            :fields="currentUserStatisticsFields.filter((i, index) => index > 0)"
            table="Current User Statistics"
            @reactivity="key += 1"
            id="cus"
        />
    </div>
</template>
<script>
import CommonTable from "@/components/tables/CommonTable";
import Ripple from "vue-ripple-directive";
import axios from "@axios";
import currentUserStatisticsFields from "./currentUserStatisticsFields";
import {
    formatMoney,
    exportFile,
    formatNumber,
    setFilterInLocalStorage,
    getFilterInLocalStorage,
    removeFilterInLocalStorage,
    isFilteredByRegion,
    getClientEmail,
    mergeTwoClients,
} from "@/helper";
import FilterPills from "@/components/tables/LevelPills.vue";
import RowCountPill from "@/components/tables/RowCountPill.vue";
import Dot from "@/components/Dot";
import TableSettingsModal from "@/components/TableSettingModal";
import NameField from "@/components/tables/NameField.vue";
import vSelect from "vue-select";

const TABLE_NAME = "CurrentUserStatistics";

export default {
    components: {
        CommonTable,
        FilterPills,
        Dot,
        TableSettingsModal,
        RowCountPill,
        NameField,
        vSelect,
    },
    props: ["current_user_statistics", "isLoading"],
    directives: {
        Ripple,
    },
    data() {
        return {
            key: 1,
            exportFormat: "csv",
            labels: [
                "CurrentCredit",
                "CurrentEquity",
                "Withdrawable",
                "ActualPL",
                "%ChangeEquity",
                "PLYesterday",
                "PLThisMonth",
                "PLLastMonth",
                "PLOverall",
                "#LotsThisMonth",
                "%ProfitableClosedTrade",
                "AVGProfitPerClosedTradeThisMonth",
                "%ProfitableTotalTrade",
                "AVGFloatingPLPerOpenTrade",
                "YesterdayEquity",
                "OverallProfitableDeals",
                "OverallDeals",
                "ClosedPLThisMonthBeforeToday",
                "TotalClosedPLToday",
                "ClosedDealsThisMonth",
                "CountOfOpenTrades",
                "TotalFloatingPL",
            ],
            currentUserStatisticsFields,
            groupLevels: [],
            activeLevels: [],
            collapsedSet: [],
            maxDisplayRowCount: 19,
            isFilterShown: false,
            isFilterZeroShown: true,
            filterValues: {},
            canFilterOutZeroValues: true,
            filterOutZeroValues: {},
            field_sort: "CurrentEquity",
            isDesc: true,
            group_organization: {},
        };
    },

    computed: {
        companyCurrency() {
            const riskMangementSettings = this.$store.getters["companySettings/settingsByPath"](["risk_management"]);
            if (riskMangementSettings.settings) return riskMangementSettings.settings.currency;
            return "";
        },
        fields() {
            let restrict = JSON.parse(localStorage.getItem("Current User Statistics Settings"));
            if (this.key && restrict) return currentUserStatisticsFields.filter((i) => !restrict.includes(i.label));
            return currentUserStatisticsFields;
        },

        fieldsMap() {
            return this.fields.reduce((res, val) => {
                return Object.assign(res, { [val.key]: val });
            }, {});
        },

        levels() {
            let result = this.groupLevels.map((levelName, index) => ({
                name: levelName,
                value: (item) => item.properties.path[index],
            }));
            return [
                ...result,
                {
                    name: "Client",
                    value: (item) =>
                        `${item.properties.mt_account?.name ?? ""} ${item.properties.mt_account?.email ?? ""}`,
                },
                { name: "Account", value: (item) => `${item.properties.server}-${item.properties.login}` },
            ];
        },

        whichpath() {
            let res = "path";
            if (!isFilteredByRegion(this.activeLevels, this.groupLevels.length)) {
                res = "subpath";
            }
            return res;
        },

        dataTotal() {
            // console.log(this.labels, this.dataAfterGroup.filter(item => item.level == 5))
            // console.log(this.labels, this.dataAfterGroup.filter(item => item.level == this.groupLevels.length + 2))
            let result = this.calculateSum(
                this.labels,
                this.dataAfterGroup.filter((item) => item.level == this.groupLevels.length + 2)
            );
            // console.log(result)
            return result;
        },

        dataAfterCollapsed() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFormat));
            this.collapsedSet.forEach((path) => {
                results = results.filter((item) => !this.isChildOfParent(item[this.whichpath], path));
            });
            return results;
        },

        dataAfterFormat() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFilteredByZero));
            results = results.map((item) => {
                this.labels.forEach((field) => {
                    item[field] = formatMoney(item[field], 2);
                });
                return item;
            });
            return results;
        },

        dataAfterFilteredByZero() {
            let results = JSON.parse(JSON.stringify(this.dataAfterFiltered));

            Object.keys(this.filterOutZeroValues).forEach((key) => {
                if (this.filterOutZeroValues[key]) {
                    results = results.filter((item) => {
                        if (key != "Risk") return !isNaN(parseFloat(item[key])) && parseFloat(item[key]) !== 0;
                        else return item[key] !== 1;
                    });
                }
            });
            let validPaths = results.map((item) => item[this.whichpath]);
            results = JSON.parse(JSON.stringify(this.dataAfterSort));
            results = results.filter((item) => {
                let bool = false;
                validPaths.forEach((path) => {
                    if (this.isChildOfParent(path, item[this.whichpath]) || path == item[this.whichpath]) {
                        bool = true;
                        return;
                    }
                });
                return bool;
            });
            return results;
        },

        dataAfterFiltered() {
            let results = JSON.parse(JSON.stringify(this.dataAfterSort));
            Object.keys(this.filterValues).forEach((key) => {
                let field = this.fields.find((field) => field.key == key);
                if (field?.filter?.filterFunc) {
                    // console.log(results, this.filterValues[key])
                    results = field.filter.filterFunc(results, this.filterValues[key]);
                } else {
                    if (this.filterValues[key]) {
                        results = results.filter((item) => {
                            return (this.formatValue(item, key) ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(this.filterValues[key].toString().toLowerCase());
                        });
                    }
                }
            });
            let validPaths = results.map((item) => item[this.whichpath]);
            results = JSON.parse(JSON.stringify(this.dataAfterSort));
            results = results.filter((item) => {
                let bool = false;
                validPaths.forEach((path) => {
                    if (this.isChildOfParent(path, item[this.whichpath]) || path == item[this.whichpath]) {
                        bool = true;
                        return;
                    }
                });
                return bool;
            });
            return results;
        },

        dataAfterSort() {
            let desc_or_asc = this.isDesc ? -1 : 1;
            let result = [];
            let j = 0;
            this.activeLevels.forEach((level, index) => {
                let pos_or_neg = index == 0 ? desc_or_asc : -desc_or_asc;
                let data = this.dataAfterGroup.filter((datum) => datum.level == level);
                data.sort((a, b) => {
                    let x = a[this.field_sort];
                    let y = b[this.field_sort];
                    if (this.fieldsMap[this.field_sort].displayAbsolute) {
                        x = Math.abs(x);
                        y = Math.abs(y);
                    }
                    return pos_or_neg * (x < y ? -1 : x > y ? 1 : 0);
                });

                // Check if table is filtered by region, if not we have to merge client with same email (diffrent region)
                if (
                    !isFilteredByRegion(this.activeLevels, this.groupLevels.length) &&
                    level === this.groupLevels.length + 1
                ) {
                    let noDuplicatedData = {};
                    data.forEach((datum) => {
                        if (noDuplicatedData[datum.name]) {
                            noDuplicatedData[datum.name] = mergeTwoClients(
                                noDuplicatedData[datum.name],
                                datum,
                                this.fields
                            );
                        } else noDuplicatedData[datum.name] = datum;
                    });
                    data = Object.values(noDuplicatedData);
                }

                if (index == 0) {
                    result = result.concat(data);
                    j = level;
                } else {
                    // Find correct index and insert the value
                    // console.log(level, index, j, data)
                    data.forEach((current) => {
                        result.forEach((datum, index) => {
                            if (
                                datum.level == j &&
                                this.isChildOfParent(current[this.whichpath], datum[this.whichpath])
                            ) {
                                result.splice(index + 1, 0, current);
                            }
                        });
                    });
                    j = level;
                }
            });
            // console.log("dataAfterSort", results)
            return result;
        },

        dataAfterGroup() {
            let results = [];
            // console.log(this.items)

            this.levels.forEach((level, index) => {
                if (this.activeLevels.includes(index + 1)) {
                    let distinctValues = [
                        ...new Set(
                            this.items.map((item) => {
                                let path = item.path.split("/").slice(0, index + 1);
                                if (this.whichpath == "subpath") {
                                    path = path.slice(Math.min(...this.activeLevels) - 1);
                                }
                                return path.join("/");
                            })
                        ),
                    ];

                    // console.log(distinctValues)

                    results.push(
                        ...distinctValues.map((path) => {
                            let name = path.split("/");
                            name = this.group_organization[path]?.name ?? name[name.length - 1];

                            let data = this.items.filter(
                                (item) =>
                                    this.isChildOfParent(item[this.whichpath], path) || item[this.whichpath] == path
                            );
                            // console.log(path, data)
                            let tmp = this.calculateSum(this.labels, data);

                            if (index == this.levels.length - 1) {
                                tmp = {
                                    ...data[0],
                                    ...tmp,
                                };
                                // console.log(index, data, tmp)
                            }

                            // console.log(level, path)

                            return {
                                name: name,
                                ...tmp,
                                level: index + 1,
                                isUncollapsible: index + 1 === Math.max(...this.activeLevels),
                                [this.whichpath]: path,
                            };
                        })
                    );
                }
            });
            // console.log("dataAfterGroup", results)
            return results;
        },

        items() {
            // return mockdata
            // console.log(this.current_user_statistics.filter(item => item?.properties?.path))
            let res = this.current_user_statistics
                .filter((item) => item?.properties?.path)
                .map((item) => {
                    let groupPath = item.properties.path.join("/");
                    let group = item.properties.group.split("\\");
                    Object.keys(this.group_organization).forEach((key) => {
                        if (key === groupPath) group = this.group_organization[key].name;
                    });
                    let values = JSON.parse(JSON.stringify(item.value));
                    values = this.calculateSpecialValues(values);
                    let tmp = this.labels.reduce((obj, key) => {
                        obj[key] = values?.[key] ?? NaN;
                        return obj;
                    }, {});
                    // console.log(values, tmp)
                    let path = this.levels.map((level) => level.value(item));
                    let subpath = path.slice(Math.min(...this.activeLevels) - 1);
                    return {
                        ...tmp,
                        level: this.groupLevels.length + 2,
                        path: path.join("/"),
                        subpath: subpath.join("/"),
                        name: item.properties.server + "-" + item.properties.login,
                        group,
                    };
                });
            // console.log(JSON.stringify(res))
            // console.log(res)
            return res;
        },
        exportData() {
            return [
                [...this.fields.map((field) => field.label)],
                ...this.items.map((item) => {
                    return this.fields.map((field) => {
                        if (!isNaN(item[field.key])) return formatNumber(item[field.key], 2);
                        return item[field.key];
                    });
                }),
            ];
        },
    },

    methods: {
        formatMoney,
        exportFile,

        isChildOfParent(child, parent) {
            let child_path = child.split("/");
            let parent_path = parent.split("/");
            return (
                child_path.length > parent_path.length && child_path.slice(0, parent_path.length).join("/") == parent
            );
        },

        sortBy(field) {
            if (field) {
                this.field_sort = field;
                this.isDesc = !this.isDesc;
            }
        },

        initFilter() {
            if (getFilterInLocalStorage(TABLE_NAME)) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME);

                this.isFilterShown = true;
                this.$refs.table.initFilter(filterValues);
            }
            if (getFilterInLocalStorage(TABLE_NAME + "Zero")) {
                let filterValues = getFilterInLocalStorage(TABLE_NAME + "Zero");
                this.isFilterZeroShown = filterValues.show ? true : false;
                this.$refs.table.initFilterZero(filterValues);
            }
        },

        toggleFilterZero() {
            this.isFilterZeroShown = !this.isFilterZeroShown;
            if (this.isFilterZeroShown) {
                let filterOutZeroValues = getFilterInLocalStorage(TABLE_NAME + "Zero") || {};

                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: true,
                });
            } else {
                let filterOutZeroValues = getFilterInLocalStorage(TABLE_NAME + "Zero") || {};

                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: false,
                });
            }
        },
        toggleFilter() {
            this.isFilterShown = !this.isFilterShown;
            if (this.isFilterShown) {
                setFilterInLocalStorage(TABLE_NAME, {});
            } else {
                removeFilterInLocalStorage(TABLE_NAME);
            }
        },

        clearFilter() {
            this.$refs.table.clearFilter();
        },

        filterChange(filterValues) {
            Object.keys(filterValues).forEach((key) => {
                this.$set(this.filterValues, key, filterValues[key]);
            });
            if (this.isFilterShown) setFilterInLocalStorage(TABLE_NAME, filterValues);
        },

        filterClear(filterValues) {
            Object.keys(filterValues).forEach((key) => {
                this.$set(this.filterValues, key, filterValues[key]);
            });
            if (this.isFilterShown) setFilterInLocalStorage(TABLE_NAME, filterValues);
        },

        handleFilterOutZeroValues(filterOutZeroValues) {
            Object.keys(filterOutZeroValues).forEach((key) => {
                if (key != "show") this.$set(this.filterOutZeroValues, key, filterOutZeroValues[key]);
            });
            if (this.isFilterZeroShown)
                setFilterInLocalStorage(TABLE_NAME + "Zero", {
                    ...filterOutZeroValues,
                    show: this.isFilterZeroShown,
                });
        },

        calculateSum(labels, data) {
            // let specialLabels = ["%ProfitableClosedTrade", "AVGProfitPerClosedTradeThisMonth", "%ProfitableTotalTrade", "AVGFloatingPLPerOpenTrade"]
            let result = labels.reduce((obj, key) => {
                if (data.length === 1) {
                    obj[key] = data[0][key];
                } else {
                    obj[key] = data.reduce((sum, item) => {
                        // if (key == "%ChangeEquity")
                        //   console.log(item[key])
                        return sum + (isNaN(item[key]) ? 0 : item[key]);
                    }, 0);
                    // if (key == "%ChangeEquity")
                    //   console.log(key, obj[key])
                }
                return obj;
            }, {});
            // if (data.length > 1)
            //   console.log(data, result["%ChangeEquity"])
            result = this.calculateSpecialValues(result);
            // if (data.length > 1)
            //   console.log(result["%ChangeEquity"])
            return result;
        },

        calculateSpecialValues(values) {
            let item = { ...values };
            // if (item["%ChangeEquity"] == 0 && !item["YesterdayEquity"])
            //   console.log(item)
            if (item["YesterdayEquity"] && !isNaN(item["YesterdayEquity"]) && !isNaN(item["ActualPL"])) {
                item["%ChangeEquity"] = (item["ActualPL"] * 100) / item["YesterdayEquity"];
            }
            item["%ProfitableClosedTrade"] = item["OverallDeals"]
                ? (item["OverallProfitableDeals"] * 100) / item["OverallDeals"]
                : 0;
            item["AVGProfitPerClosedTradeThisMonth"] = item["ClosedDealsThisMonth"]
                ? (item["ClosedPLThisMonthBeforeToday"] + item["TotalClosedPLToday"]) / item["ClosedDealsThisMonth"]
                : 0;
            item["%ProfitableTotalTrade"] =
                item["OverallDeals"] + item["CountOfOpenTrades"] > 0
                    ? (item["OverallProfitableDeals"] * 100) / (item["OverallDeals"] + item["CountOfOpenTrades"])
                    : 0;
            item["AVGFloatingPLPerOpenTrade"] =
                item["CountOfOpenTrades"] > 0 ? item["TotalFloatingPL"] / item["CountOfOpenTrades"] : 0;
            return item;
        },

        isItemUncollapsible(item) {
            return item.level == Math.max(...this.activeLevels);
        },

        collapse(item) {
            this.collapsedSet.push(item[this.whichpath]);
        },

        uncollapse(item) {
            this.collapsedSet = this.collapsedSet.filter((path) => path != item[this.whichpath]);
        },

        calcNamePadding(level) {
            return 25 * this.activeLevels.indexOf(level);
        },

        toggleLevel(level) {
            // console.log(level, this.activeLevels)
            this.collapsedSet = this.collapsedSet.filter((path) => path.split("/").length >= level);
        },

        getAllChildren(node, path) {
            let result = { [path]: node.settings };
            if (node.children) {
                Object.keys(node.children).forEach((key) => {
                    result = { ...result, ...this.getAllChildren(node.children[key], path + "/" + key) };
                });
            }
            return result;
        },
    },

    mounted() {
        this.initFilter();
        // this.groupLevels = [
        //     "Company",
        //     "Region",
        //     "Team"
        // ]
        // this.activeLevels = [this.groupLevels.length + 2]
        axios
            // .get(`${process.env.VUE_APP_API_URL}site_settings/risk_management/group_levels`)
            // mockData
            .get("/GroupLevel/data")
            .then((res) => {
                this.groupLevels = res.data.settings;
                this.activeLevels = [this.groupLevels.length + 2];
            })
            .catch((err) => console.error(err));

        axios
            // .get(`${process.env.VUE_APP_API_URL}site_settings/risk_management/group_organization?recursive`)
            //? mockData
            .get("/GroupOrganization/data")
            .then((res) => {
                this.group_organization = {};
                if (res.data?.children) {
                    Object.keys(res.data.children).forEach((key) => {
                        this.group_organization = {
                            ...this.group_organization,
                            ...this.getAllChildren(res.data.children[key], key),
                        };
                    });
                }
            })
            .catch((err) => console.error(err));
    },
};
</script>
